import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getContentById, getContentBySlug, getImageUrl } from '../services/api';
import { processArticleHtml } from '../services/htmlProcessor';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import { slugToArticleId } from '../services/slugify';

function ArticlePage() {
  const { slug } = useParams();
  const id = slugToArticleId(slug);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadContent = async () => {
    try {
      setLoading(true);
      // If slug ends with a numeric ID use the fast ID lookup, otherwise look up by slug
      const response = id !== null
        ? await getContentById(id)
        : await getContentBySlug(slug);
      if (response.success) {
        setContent(response.data);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const extractFirstImage = (htmlContent, savedImageUrl) => {
    // Use saved imageUrl if available
    if (savedImageUrl) {
      return getImageUrl(savedImageUrl);
    }
    
    if (!htmlContent) return null;
    
    // Try to extract first image URL from HTML
    const imgMatch = htmlContent.match(/<img[^>]+src=["']([^"']+)["']/i);
    if (imgMatch && imgMatch[1]) {
      return imgMatch[1];
    }
    
    // Try to find og:image meta tag
    const ogImageMatch = htmlContent.match(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i);
    if (ogImageMatch && ogImageMatch[1]) {
      return ogImageMatch[1];
    }
    
    return null;
  };

  const estimateReadTime = (htmlContent) => {
    if (!htmlContent) return 1;
    const text = htmlContent.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return minutes;
  };

  const cleanHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    // Remove style tags and their content
    let cleaned = htmlContent.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    
    // Remove script tags and their content
    cleaned = cleaned.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    
    // Remove meta tags
    cleaned = cleaned.replace(/<meta[^>]*>/gi, '');
    
    // Remove link tags (stylesheets, etc.)
    cleaned = cleaned.replace(/<link[^>]*>/gi, '');
    
    // Remove head tags and their content
    cleaned = cleaned.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
    
    // Remove html and body wrapper tags (keep content)
    cleaned = cleaned.replace(/<\/?html[^>]*>/gi, '');
    cleaned = cleaned.replace(/<\/?body[^>]*>/gi, '');
    
    return cleaned;
  };

  const removeFirstImage = (htmlContent) => {
    if (!htmlContent) return '';
    // Remove only the FIRST img tag to avoid duplication with hero image
    return htmlContent.replace(/<img[^>]*>/i, '');
  };

  if (loading) {
    return (
      <div className="news-portal">
        <SiteHeader />
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading article...</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="news-portal">
        <SiteHeader />
        <div className="loading-container">
          <h3>Article not found</h3>
          <p>The article you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const thumbnailUrl = extractFirstImage(content.htmlContent, content.imageUrl);
  const readTime = estimateReadTime(content.htmlContent);

  return (
    <div className="news-portal article-view">
      <SiteHeader />

      {/* Main Content */}
      <main className="news-main">
        <div className="container">
          <div className="news-layout">
            {/* Main Article Column */}
            <div className="main-column">
              <article className="hero-story" style={{ marginBottom: 0 }}>
                <header className="hero-content" style={{ paddingBottom: '20px' }}>
                  <h1 className="hero-title" style={{ fontSize: '42px', marginBottom: '20px' }}>{content.title || 'Untitled Article'}</h1>
                  
                  <div className="hero-meta">
                    <span className="meta-time">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '5px' }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      {formatDate(content.createdAt)}
                    </span>
                    <span className="meta-time">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '5px' }}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      {readTime} min read
                    </span>
                  </div>
                </header>

                {thumbnailUrl && (
                  <div className="hero-image" style={{ height: '500px' }}>
                    <img src={thumbnailUrl} alt={content.title} />
                  </div>
                )}

                <div className="hero-content">
                  <div 
                    className="article-content" 
                    dangerouslySetInnerHTML={{ __html: processArticleHtml(cleanHtmlContent(content.htmlContent)) }}
                  />
                </div>
              </article>
            </div>

            {/* Sidebar */}
            <aside className="sidebar-column">
              <div className="sidebar-section">
                <h3 className="sidebar-title">Related Articles</h3>
                <p className="sidebar-empty">Check back soon for more articles</p>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default ArticlePage;
